import { useEffect, useRef, useState, useCallback } from 'react';

const MUSIC_URL = 'https://tnkplkzwavxekhgpnqki.supabase.co/storage/v1/object/public/music/cozy-christmas-waltz-449734.mp3';

export const useWrappedMusic = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const wasPlayingBeforeHidden = useRef(false);

  // Initialize and start audio immediately on mount (muted)
  useEffect(() => {
    const audio = new Audio(MUSIC_URL);
    audio.loop = true;
    audio.volume = 0.25;
    audio.muted = true; // Start muted - browsers allow this
    audio.preload = 'auto';
    audioRef.current = audio;

    // Start playing immediately (muted)
    audio.play()
      .then(() => {
        setIsPlaying(true);
        
        // Try to unmute after a short delay
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.muted = false;
          }
        }, 100);
      })
      .catch(() => setIsPlaying(false));

    // Fallback: unmute on first user interaction
    const handleInteraction = () => {
      if (audioRef.current) {
        audioRef.current.muted = false;
        if (audioRef.current.paused) {
          audioRef.current.play().catch(() => {});
        }
      }
    };

    // Handle visibility change - pause when backgrounded
    const handleVisibilityChange = () => {
      if (!audioRef.current) return;
      
      if (document.hidden) {
        // Store if we were playing before hiding
        wasPlayingBeforeHidden.current = !audioRef.current.paused && !audioRef.current.muted;
        audioRef.current.pause();
      } else {
        // Resume if we were playing before
        if (wasPlayingBeforeHidden.current) {
          audioRef.current.play().catch(() => {});
        }
      }
    };

    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      audio.pause();
      audio.src = '';
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setIsMuted(prev => !prev);
    }
  }, []);

  return { isMuted, toggleMute, isPlaying };
};
